/**
 * Atoll shute event registration engine.
 * Manages the queue of event listener registrations between components
 * and the shute server.
 *
 * Two exports:
 * - registerListenerQueue: synchronous decision logic — queues event registrations
 * - processQueueThunk: async Redux thunk — processes the queue, calls /events/register
 *
 * Moved from hooks/useEventNotifier.ts — renamed because neither export is a React hook.
 * sSputnikSessionID renamed to sSessionID throughout.
 *
 * Dependency contract: consuming project's Redux store must include shuteSlice.
 * Requires redux/store.ts exporting RootState.
 */

import type { ThunkDispatch } from 'redux-thunk';
import {
  addPendingEventListener,
  removeQueuedEventListener,
  addQueuedEventListener,
  QueuedEventListener,
  removePendingEventListener,
  setListenerContext,
} from "./redux/shuteSlice";
// registerEventListeners moved from services/atollapi.ts to atoll/atollApi.ts
import { registerEventListeners } from "./atollApi";
import { RootState } from "../../services/redux/store";
// actions moved to atoll/redux/actions/
import { shuteSliceActions } from "./redux/actions/interfaces";


interface RegisterListenerQueueProps {
  psPage: string;
  ptWatchEvents: string[];
  shouldExecute: boolean;
  psPageSubContext: string;
  psAction: 'add' | 'remove';
  ptCurrentEventListeners: any;
  dispatch: ThunkDispatch<RootState, any, shuteSliceActions>;
  sSessionID: string;
  tPendingEventListeners: string[];
  tQueuedEventListeners: QueuedEventListener[];
  isEventNotInQueuedListeners: boolean;
}

const queueListeners = async ({
  psPage,
  ptWatchEvents,
  shouldExecute,
  psPageSubContext,
  psAction,
  ptCurrentEventListeners,
  dispatch,
  sSessionID,
  tPendingEventListeners,
  tQueuedEventListeners,
  isEventNotInQueuedListeners,
}: RegisterListenerQueueProps): Promise<boolean> => {
  try {
    if (psAction == 'add') {
      if (ptWatchEvents.some(item => !ptCurrentEventListeners.includes(item))
        && ptWatchEvents.some(item => !tPendingEventListeners.includes(item))
        && ptWatchEvents.some((event) => tQueuedEventListeners.every((listenerObj) => !listenerObj.watchevents.includes(event)))
      ) {
        dispatch(addQueuedEventListener({ page: psPage, watchevents: ptWatchEvents, action: 'add', sessionid: sSessionID, psPageSubContext: psPageSubContext }));
        return true;
      }
    } else {
      if (ptWatchEvents.some(item => !ptCurrentEventListeners.includes(item))) {
        dispatch(addQueuedEventListener({ page: psPage, watchevents: ptWatchEvents, action: 'remove', sessionid: sSessionID, psPageSubContext: psPageSubContext }));
        return true;
      } else if (ptWatchEvents.some(item => !tPendingEventListeners.includes(item))
        || isEventNotInQueuedListeners) {
        dispatch(removeQueuedEventListener({ page: psPage, watchevents: ptWatchEvents, action: 'add', sessionid: sSessionID, psPageSubContext: psPageSubContext }));
        return true;
      }
    }
    return false;
  } catch (error) {
    console.error(error);
    return false;
  }
};

export const registerListenerQueue = ({
  psPage,
  ptWatchEvents,
  shouldExecute,
  psPageSubContext,
  psAction,
  ptCurrentEventListeners,
  dispatch,
  sSessionID,
  tPendingEventListeners,
  tQueuedEventListeners,
  isEventNotInQueuedListeners,
}: RegisterListenerQueueProps): boolean => {
  try {
    if (!shouldExecute) return false;

    const outcome = queueListeners({
      psPage,
      ptWatchEvents,
      shouldExecute,
      psPageSubContext,
      psAction,
      ptCurrentEventListeners,
      dispatch,
      sSessionID,
      tPendingEventListeners,
      tQueuedEventListeners,
      isEventNotInQueuedListeners,
    });

    return true;
  } catch (oError) {
    console.error(oError);
    return false;
  }
};

export const processQueueThunk = () => async (thunkDispatch: ThunkDispatch<RootState, undefined, shuteSliceActions>, getState: () => RootState) => {
  const queuedEventListeners = getState().shute.queued_event_listeners;
  if (!queuedEventListeners || queuedEventListeners.length == 0) return;
  const aggregatedEvents: Record<string, QueuedEventListener> = {};
  const eventSubContexts: Record<string, string[]> = {};
  for (let item of queuedEventListeners) {
    if (!item.watchevents || item.watchevents.length == 0) continue;
    const key = `${item.page}_${item.sessionid}_${item.action}`;

    if (!aggregatedEvents[key]) {
      aggregatedEvents[key] = {
        page: item.page,
        sessionid: item.sessionid,
        action: item.action,
        watchevents: [],
        psPageSubContext: item.psPageSubContext
      };
    }
    thunkDispatch(removeQueuedEventListener(item));
    aggregatedEvents[key].watchevents.push(...item.watchevents);
    for (let event of item.watchevents) {
      if (!eventSubContexts[event]) {
        eventSubContexts[event] = [item.psPageSubContext];
      } else {
        eventSubContexts[event].push(item.psPageSubContext);
      }
    }
  }
  for (let key in aggregatedEvents) {
    try {
      const { page, watchevents, action, sessionid, psPageSubContext } = aggregatedEvents[key];
      const uniqueWatchEvents = [...new Set(watchevents)];
      let filteredWatchEvents;

      if (action === 'add') {
        const currentEventListeners = getState().shute.current_event_listeners;
        const pendingEventListeners = getState().shute.pending_event_listeners;

        filteredWatchEvents = uniqueWatchEvents.filter(
          (item: string) => !currentEventListeners.includes(item) && !pendingEventListeners.includes(item)
        );
      } else {
        filteredWatchEvents = uniqueWatchEvents;
      }

      if (!filteredWatchEvents || filteredWatchEvents.length === 0) continue;

      thunkDispatch(addPendingEventListener(uniqueWatchEvents));
      const ptPageEvents = getState().shute.eventContexts[page];
      await registerEventListeners(page, uniqueWatchEvents, action, sessionid, thunkDispatch, psPageSubContext, ptPageEvents);
      if (action === 'add') {
        uniqueWatchEvents.forEach(event => {
          for (let subie of eventSubContexts[event]) {
            thunkDispatch(setListenerContext({ eventAction: 'add', page: page, event: event, context: subie }));
          }
        });
      } else if (action === 'remove') {
        uniqueWatchEvents.forEach(event => {
          for (let subie of eventSubContexts[event]) {
            thunkDispatch(setListenerContext({ eventAction: 'remove', page: page, event: event, context: subie }));
          }
        });
      }
      // Remove pending status after 10 seconds
      uniqueWatchEvents.forEach(event => {
        setTimeout(() => {
          thunkDispatch(removePendingEventListener(event));
        }, 10000);
      });
    } catch (error) {
      throw error;
    }
  }
};
